import { useState, useEffect } from 'react';
import { useToast } from './use-toast';
import { useCharacterStore } from './useCharacterStore';
import { useTimeProgression } from './useTimeProgression';

interface Challenge {
  id: string;
  title: string;
  description: string;
  type: 'daily' | 'weekly';
  target: number;
  current: number;
  completed: boolean;
  reward: {
    money: number;
    experience: number;
    message: string;
  };
  icon: string;
  deadline: string;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  unlocked: boolean;
  unlockedAt?: string;
  icon: string;
  requirement: {
    type: 'streak' | 'total_correct' | 'modules_completed' | 'challenges_completed' | 'points_earned';
    value: number;
  };
  reward: {
    money: number;
    experience: number;
  };
}

const STORAGE_KEY = 'talousakatemia_challenges';
const ACHIEVEMENTS_KEY = 'talousakatemia_achievements';

// Define static achievements
const STATIC_ACHIEVEMENTS: Achievement[] = [
  {
    id: 'first_steps',
    title: 'Ensimmäiset askeleet',
    description: 'Vastaa ensimmäiseen kysymykseen oikein',
    unlocked: false,
    icon: '👶',
    requirement: { type: 'total_correct', value: 1 },
    reward: { money: 25, experience: 25 }
  },
  {
    id: 'streak_master',
    title: 'Putki-mestari',
    description: 'Saa 10 oikean vastauksen putki',
    unlocked: false,
    icon: '🔥',
    requirement: { type: 'streak', value: 10 },
    reward: { money: 100, experience: 100 }
  },
  {
    id: 'knowledge_seeker',
    title: 'Tiedonjanoisin',
    description: 'Vastaa 50 kysymykseen oikein',
    unlocked: false,
    icon: '📚',
    requirement: { type: 'total_correct', value: 50 },
    reward: { money: 200, experience: 200 }
  },
  {
    id: 'challenge_hunter',
    title: 'Haasteiden metsästäjä',
    description: 'Suorita 5 haastetta',
    unlocked: false,
    icon: '🎯',
    requirement: { type: 'challenges_completed', value: 5 },
    reward: { money: 150, experience: 150 }
  },
  {
    id: 'expert_learner',
    title: 'Asiantuntija-oppija',
    description: 'Vastaa 100 kysymykseen oikein',
    unlocked: false,
    icon: '🎓',
    requirement: { type: 'total_correct', value: 100 },
    reward: { money: 500, experience: 500 }
  },
  {
    id: 'money_maker',
    title: 'Rahantekijä',
    description: 'Ansaitse 1000€ pisteistä',
    unlocked: false,
    icon: '💰',
    requirement: { type: 'points_earned', value: 1000 },
    reward: { money: 300, experience: 300 }
  }
];

export const useChallengeSystem = () => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>(STATIC_ACHIEVEMENTS);
  const [stats, setStats] = useState({
    totalCorrectAnswers: 0,
    modulesCompleted: 0,
    challengesCompleted: 0,
    totalPointsEarned: 0,
    bestStreak: 0
  });

  const { toast } = useToast();
  const { addExperience } = useCharacterStore();
  const { earnMoney } = useTimeProgression();

  // Challenge data no longer persisted to prevent user data sharing

  // Generate weekly challenges
  const generateWeeklyChallenges = (): Challenge[] => {
    const now = new Date();
    const weekEnd = new Date(now);
    weekEnd.setDate(now.getDate() + (7 - now.getDay())); // Next Sunday
    weekEnd.setHours(23, 59, 59, 999);

    return [
      {
        id: `weekly_correct_${now.getTime()}`,
        title: 'Viikon tarkka-ampuja',
        description: 'Vastaa 15 kysymykseen oikein tällä viikolla',
        type: 'weekly',
        target: 15,
        current: 0,
        completed: false,
        reward: { money: 200, experience: 200, message: 'Viikon haaste suoritettu! +200€ ja +200 XP' },
        icon: '🎯',
        deadline: weekEnd.toISOString()
      },
      {
        id: `weekly_streak_${now.getTime()}`,
        title: 'Viikon putkikuningas',
        description: 'Saa 7 oikean vastauksen putki',
        type: 'weekly',
        target: 7,
        current: 0,
        completed: false,
        reward: { money: 150, experience: 150, message: 'Viikon putki-haaste suoritettu! +150€ ja +150 XP' },
        icon: '🔥',
        deadline: weekEnd.toISOString()
      },
      {
        id: `weekly_modules_${now.getTime()}`,
        title: 'Viikon ahkera opiskelija',
        description: 'Suorita 3 moduulin osio',
        type: 'weekly',
        target: 3,
        current: 0,
        completed: false,
        reward: { money: 100, experience: 100, message: 'Viikon opiskeluhaaste suoritettu! +100€ ja +100 XP' },
        icon: '📖',
        deadline: weekEnd.toISOString()
      }
    ];
  };

  // Initialize challenges if empty or expired
  useEffect(() => {
    const now = new Date();
    const hasValidChallenges = challenges.some(c => new Date(c.deadline) > now);
    
    if (challenges.length === 0 || !hasValidChallenges) {
      setChallenges(generateWeeklyChallenges());
    }
  }, [challenges]);

  const checkAchievements = (newStats: typeof stats) => {
    achievements.forEach(achievement => {
      if (achievement.unlocked) return;

      let shouldUnlock = false;
      switch (achievement.requirement.type) {
        case 'total_correct':
          shouldUnlock = newStats.totalCorrectAnswers >= achievement.requirement.value;
          break;
        case 'streak':
          shouldUnlock = newStats.bestStreak >= achievement.requirement.value;
          break;
        case 'modules_completed':
          shouldUnlock = newStats.modulesCompleted >= achievement.requirement.value;
          break;
        case 'challenges_completed':
          shouldUnlock = newStats.challengesCompleted >= achievement.requirement.value;
          break;
        case 'points_earned':
          shouldUnlock = newStats.totalPointsEarned >= achievement.requirement.value;
          break;
      }

      if (shouldUnlock) {
        setAchievements(prev => prev.map(a => 
          a.id === achievement.id 
            ? { ...a, unlocked: true, unlockedAt: new Date().toISOString() }
            : a
        ));

        // Use earnMoney for wallet - goes to player_financial_status
        // Fire-and-forget is acceptable for achievement rewards, but log any errors
        earnMoney(achievement.reward.money).catch(err => console.warn('Achievement earnMoney failed:', err));
        addExperience(achievement.reward.experience);

        // Achievement unlocked - rewards given silently without toast notification
      }
    });
  };

  const updateProgress = (type: 'correct_answer' | 'module_completed' | 'streak_achieved', value: number = 1) => {
    const newStats = { ...stats };
    
    switch (type) {
      case 'correct_answer':
        newStats.totalCorrectAnswers += 1;
        newStats.totalPointsEarned += value;
        
        // Update weekly challenges
        setChallenges(prev => prev.map(challenge => {
          if (challenge.completed) return challenge;
          
          if (challenge.id.includes('weekly_correct')) {
            const newCurrent = Math.min(challenge.current + 1, challenge.target);
            const completed = newCurrent >= challenge.target;
            
            if (completed && !challenge.completed) {
              // Use earnMoney for wallet - goes to player_financial_status
              earnMoney(challenge.reward.money).catch(err => console.warn('Challenge earnMoney failed:', err));
              addExperience(challenge.reward.experience);
              // Challenge completed - rewards given silently
              newStats.challengesCompleted += 1;
            }
            
            return { ...challenge, current: newCurrent, completed };
          }
          return challenge;
        }));
        break;
        
      case 'module_completed':
        newStats.modulesCompleted += 1;
        
        setChallenges(prev => prev.map(challenge => {
          if (challenge.completed) return challenge;
          
          if (challenge.id.includes('weekly_modules')) {
            const newCurrent = Math.min(challenge.current + 1, challenge.target);
            const completed = newCurrent >= challenge.target;
            
            if (completed && !challenge.completed) {
              // Use earnMoney for wallet - goes to player_financial_status
              earnMoney(challenge.reward.money).catch(err => console.warn('Challenge earnMoney failed:', err));
              addExperience(challenge.reward.experience);
              // Challenge completed - rewards given silently
              newStats.challengesCompleted += 1;
            }
            
            return { ...challenge, current: newCurrent, completed };
          }
          return challenge;
        }));
        break;
        
      case 'streak_achieved':
        newStats.bestStreak = Math.max(newStats.bestStreak, value);
        
        setChallenges(prev => prev.map(challenge => {
          if (challenge.completed) return challenge;
          
          if (challenge.id.includes('weekly_streak') && value >= challenge.target) {
            if (!challenge.completed) {
              // Use earnMoney for wallet - goes to player_financial_status
              earnMoney(challenge.reward.money).catch(err => console.warn('Challenge earnMoney failed:', err));
              addExperience(challenge.reward.experience);
              // Challenge completed - rewards given silently
              newStats.challengesCompleted += 1;
            }
            
            return { ...challenge, current: challenge.target, completed: true };
          }
          return challenge;
        }));
        break;
    }
    
    setStats(newStats);
    checkAchievements(newStats);
  };

  const getActiveChallenges = () => {
    const now = new Date();
    return challenges.filter(c => new Date(c.deadline) > now);
  };

  const getCompletedChallenges = () => {
    return challenges.filter(c => c.completed);
  };

  const getUnlockedAchievements = () => {
    return achievements.filter(a => a.unlocked);
  };

  const getPendingAchievements = () => {
    return achievements.filter(a => !a.unlocked);
  };

  return {
    challenges: getActiveChallenges(),
    achievements,
    stats,
    updateProgress,
    getCompletedChallenges,
    getUnlockedAchievements,
    getPendingAchievements,
    totalAchievements: achievements.length,
    unlockedCount: achievements.filter(a => a.unlocked).length
  };
};